import React, {useState, useRef, useEffect} from "react";
import {Link, useNavigate} from "react-router-dom";
import FormatTimeStr from "../../helpers/FormatTimeStr";
import InterfaceContactInfo from "../../interface/InterfaceContactInfo";
import InterfaceBooking from "../../interface/InterfaceBooking";
import api from "../../helpers/api";
import {SVVInformationMessage} from "@komponentkassen/svv-information-message";
import {useApptekster} from "../../apptekst-context";
import {SVVButtonRow, SVVButtonCustom, SVVButton, SVVButtonCancel} from "@komponentkassen/svv-button";
import {ReminderString} from "../../helpers/SharedTypes";
import {SVVDefinitionList, SVVDefinitionListItem} from "@komponentkassen/svv-definition-list";
import {SVVBodyText, SVVHeading} from "@komponentkassen/svv-text";

const Confirmation = (props: {
  originalBooking: InterfaceBooking;
  newBooking?: InterfaceBooking;
  contactInfo: InterfaceContactInfo;
  dateAndTime: any;
  setBooking: any;
  setNewBooking?: any;
  setNavState: any;
  abort: any;
  confirmText: string,
  sendReminderTo: ReminderString
  avbrytUrl?: string
}) => {
  const t = useApptekster();
  const navigate = useNavigate();
  const serviceName = props.newBooking?.serviceName ? props.newBooking?.serviceName : props.originalBooking.serviceName;
  const sectionName = props.newBooking?.sectionName ? props.newBooking?.sectionName : props.originalBooking.sectionName;
  const [noChangesMadeOnBooking, setNoChangesMadeOnBooking] = useState<boolean>(false);
  const [showSubmitErrorMessage, setShowSubmitErrorMessage] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [errorCode, setErrorCode] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showIsSubmittingMessage, setShowIsSubmittingMessage] = useState<boolean>(false);

  const headingRef = useRef<HTMLHeadingElement | null>(null)

  useEffect(() => {
    if (headingRef?.current) {
      headingRef.current.focus()
    }
  }, [headingRef])

  const handleApiError = () => {
    setIsSubmitting(false)
    setShowSubmitErrorMessage(true)
    setShowIsSubmittingMessage(false)
  }

  const {
    contactInfo,
    dateAndTime,
    sendReminderTo
  } = props

  const showPhoneNumber = (sendReminderTo === "both" || sendReminderTo === "sms")
  const showEmail = (sendReminderTo === "both" || sendReminderTo === "email")
  
  const submit = (event: any) => {
    if (isSubmitting) {
      setShowIsSubmittingMessage(true)
      return
    }
    setIsSubmitting(true)
    
    event.preventDefault();
    
    if (props.originalBooking.bookingNumber && props.newBooking && props.newBooking.bookingId === 0) {
      setNoChangesMadeOnBooking(true);
      setIsSubmitting(false)
    }

    const isRebook: boolean = props.originalBooking.bookingNumber !== null && props.newBooking !== undefined && props.newBooking?.bookingId > 0;
    const isBooking: boolean = props.newBooking === undefined && props.originalBooking.bookingId !== null && props.contactInfo !== null;
    const queryParams: String[] = [];

    if (isRebook && props.newBooking !== undefined) {
      queryParams.push(encodeURIComponent('newBookingId') + '=' + encodeURIComponent(props.newBooking.bookingId));
      queryParams.push(encodeURIComponent('oldBookingId') + '=' + encodeURIComponent(props.originalBooking.bookingId));
    } else if (isBooking) {
      queryParams.push(encodeURIComponent('bookingId') + '=' + encodeURIComponent(props.originalBooking.bookingId));
    }

    for (const key in props.contactInfo) {
      const contactInfo: any = props.contactInfo;
      queryParams.push(encodeURIComponent(key) + '=' + encodeURIComponent(contactInfo[key]));
    }
    
    setShowSubmitErrorMessage(false)
    
    if (isRebook) {
      api
        .get(`/api/Timebestilling/booking/rebook?${queryParams.join("&")}`)
        .then(function (response) {
          console.table(response.data);
          props.setNewBooking(response.data);
          props.setNavState();
          navigate("/endre/kvittering");
        })
        .catch(function (error) {
          handleApiError()
        });
    } else if (isBooking) {
      api
        .get(`/api/Timebestilling/booking/complete?${queryParams.join("&")}`)
        .then((response) => {
          props.setBooking(response.data);
          props.setNavState();
          navigate("/bestill/kvittering");
        })
        .catch((error) => {
          const responseErrorCode = error.response.data[0]?.ErrorCode
          setErrorCode(responseErrorCode)
          handleApiError()
        });
    }
  };
  
  useEffect(() => {
    if(errorCode === 9){
      setErrorMessage(t("timebestilling.bestille-time-maks-antall-booking-feil"))
    }else{
      setErrorMessage(t("timebestilling.bestille-time-generisk-feil-bestill"))
    }
    //Eslint vil ha t i dependencies, men det trengs ikke
    // eslint-disable-next-line
  }, [errorCode])

  return (
    <>
      <SVVHeading
        level="h2"
        ref={headingRef}
        tabIndex={-1}
      >
        {t("timebestilling.oppsummering")}
      </SVVHeading>
      <SVVBodyText marginBottom="lg">
        {t("timebestilling.sjekk-opplysninger")}
      </SVVBodyText>

      <div className="svv-padding-bottom--px24 svv-padding-bottom-tablet-up--px32">
        <SVVDefinitionList 
          heading={t("timebestilling.timen-din")}
          headingLevel="h3"
        >
          <SVVDefinitionListItem
            term={t("timebestilling.tjeneste")}
          >
            {serviceName || t("timebestilling.ikke-valgt")}
          </SVVDefinitionListItem>
          <SVVDefinitionListItem
            term={t("timebestilling.trafikkstasjon-og-tidspunkt")}
          >
            {sectionName || t("timebestilling.ikke-valgt")} - {dateAndTime ? FormatTimeStr(dateAndTime) : t("timebestilling.ikke-valgt")}
          </SVVDefinitionListItem>
        </SVVDefinitionList>
      </div>

      <div className="svv-padding-bottom--px24 svv-padding-bottom-tablet-up--px32">
        <SVVDefinitionList 
          heading={t("timebestilling.kontaktinformasjon")}
          headingLevel="h3"
        >
          <SVVDefinitionListItem
            term={t("timebestilling.fullt-navn")}
          >
            <span data-hj-suppress>
              {contactInfo.firstName && contactInfo.lastName && `${contactInfo.firstName} ${contactInfo.lastName}`}
            </span> 
          </SVVDefinitionListItem>
          {showEmail ?
            <SVVDefinitionListItem
              term={t("timebestilling.oppsummering-epostadr")}
            >
              <span data-hj-suppress>
                {contactInfo.emailAddress}
              </span>
            </SVVDefinitionListItem>
            : <></>
          }
          {showPhoneNumber ?
            <SVVDefinitionListItem
              term={t("timebestilling.oppsummering-mobilnummer")}
            >
               <span data-hj-suppress>
                {contactInfo.phoneNumber}
               </span>
            </SVVDefinitionListItem>
            : <></>
          }
        </SVVDefinitionList>
      </div>
      <div className="svv-padding-bottom--px24 svv-padding-bottom-tablet-up--px32">
        <SVVDefinitionList 
          heading={t("timebestilling.bekreftelse-og-paminnelse")}
          headingLevel="h3"
        >
          <SVVDefinitionListItem term={t("timebestilling.bekreft-og-paminnelse")}>
            {sendReminderTo === "both" &&
              t("timebestilling.send-paminnelse-valgt-begge")
            }
            {sendReminderTo === "sms" &&
              t("timebestilling.send-paminnelse-velgt-sms")
            }
            {sendReminderTo === "email" &&
              t("timebestilling.send-paminnelse-velgt-epost")
            }
          </SVVDefinitionListItem>
        </SVVDefinitionList>
      </div>
      
      {noChangesMadeOnBooking && props.originalBooking.bookingNumber &&
        <SVVInformationMessage type="info">
          <p>
            {t("timebestilling.ikke-reg-nye-endringer")} {props.originalBooking.bookingNumber}.  
          </p>
        </SVVInformationMessage>
      }
      
      <div aria-live="assertive">
        {showSubmitErrorMessage &&
          <div className="svv-padding-vertical--px32">
            <SVVInformationMessage type="error">
              <p
                id="is-submitting-message"
                dangerouslySetInnerHTML={{__html: errorMessage}}
              >
  
              </p>
            </SVVInformationMessage>
          </div>
        }
        {showIsSubmittingMessage && 
          <div className="svv-padding-vertical--px32">
            <SVVInformationMessage type="error">
              <p id="is-submitting-message">
                {t("timebestilling.tjenesten-laster-vennligst-vent")}
              </p>
            </SVVInformationMessage>
          </div>
        }
      </div>
      
      <SVVButtonRow isFormRow>
        <SVVButton 
          type="submit"
          onClick={submit}
          loading={isSubmitting}
        >
          {props.confirmText}
        </SVVButton>
        <SVVButtonCustom 
          asElement={Link}
          href={props.originalBooking.bookingNumber ? "/endre/kontakt" : "/bestill/kontakt"}
          hrefAttribute="to"
          color="secondary"
        >
          {t("timebestilling.tilbake")}
        </SVVButtonCustom>
        {props.avbrytUrl &&
          <SVVButtonCancel
            onClick={props.abort}
            color="secondary"
          >
            {t("timebestilling.avbryt")}
          </SVVButtonCancel>
        }
      </SVVButtonRow>
    </>
  );
};

export default Confirmation;
